import React from 'react';
import { createPortal } from 'react-dom';
import SparkleTrail from '../ui/SparkleTrail';
import ChoiceCardsModal from '../ui/ChoiceCardsModal';
import ExpandedMessageModal from '../ui/ExpandedMessageModal';
import TaskCreationFlow from '../magic/TaskCreationFlow';
import InteractiveTutorialV2 from '../tutorial/InteractiveTutorialV2';
import AvatarJourney from '../tutorial/AvatarJourney';
import ChatSidebarHeader from './ChatSidebarHeader';
import ChatContextView from './ChatContextView';
import ChatTabNavigation from './ChatTabNavigation';
import ChatMessagesView from './ChatMessagesView';
import ChatHistoryView from './ChatHistoryView';
import ChatCollapsedView from './ChatCollapsedView';
import ChatContextIndicator from './ChatContextIndicator';
import ChatSettingsView from './ChatSettingsView';
import ChatStateDebugPanel from './ChatStateDebugPanel';
import { getContextualPlaceholder } from './utils/chatSidebarUtils';

/**
 * Layout component for ChatSidebar
 * Handles all the visual structure and component composition
 */
const ChatSidebarLayout = ({
  // Layout props
  darkMode,
  className,
  isTyping,
  isCollapsed,
  
  // Tab state
  activeTab,
  setActiveTab,
  showTabs,
  
  // Core chat data
  messages,
  message,
  setMessage,
  messagesEndRef,
  autoExpandingMessage,
  tourInProgress,
  setTourInProgress,
  currentContext,
  currentDomain,
  
  // Async polling state
  isPolling,
  currentProgress,
  currentProgressMessage,
  
  // Modal states
  isChoiceCardsModalOpen,
  choiceCardsData,
  isExpandedMessageModalOpen,
  expandedMessage,
  isTaskFlowActive,
  activeTaskCard,
  isTutorialModeActive,
  activeTutorialData,
  isAvatarJourneyActive,
  
  // Sparkle trail state
  isTrailActive,
  sourceElement,
  targetElement,
  activeTrail,
  
  // Settings view state
  showSidebarForm = false,
  onShowSidebarForm = () => {},
  onHideSidebarForm = () => {},
  
  // Analysis settings props
  analysisSettings = {},
  
  // Handlers
  onToggleCollapse,
  reopenLastTutorial,
  onStartNewConversation,
  onConversationReactivated,
  handleSuggestionClick,
  handleExpandMessage,
  handleInlineButtonClick,
  handleSendMessage,
  handleStopThinking,
  handleChoiceCardSelect,
  handleChoiceCardsClose,
  handleCloseExpandedMessage,
  handleTaskGenerated,
  handleStepComplete,
  handleTaskFlowComplete,
  handleTaskClose,
  handleAvatarJourneyComplete,
  handleTutorialClose,
  completeTrail,
  portalMode = false
}) => {
  if (isCollapsed) {
    return (
      <ChatCollapsedView 
        darkMode={darkMode}
        onToggleCollapse={onToggleCollapse}
        className={`
          ${className}
          md:w-12 md:h-full md:rounded-l-2xl md:border-l md:mr-2.5
          w-12 h-12 rounded-full border-0 mr-0
        `}
      />
    );
  }

  return (
    <div className={`
      flex flex-col h-full border-l shadow-xl overflow-hidden
      transition-all duration-500 relative
      ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}
      ${isTyping 
        ? 'shadow-2xl shadow-purple-500/40 border-purple-400/50' 
        : ''
      }
      ${className}
      md:w-80 md:rounded-l-2xl md:mr-2.5 md:border-l
      w-full rounded-t-2xl mr-0 border-l-0 border-t
    `}
      style={isTyping ? {
        filter: 'drop-shadow(0 0 20px rgba(147, 51, 234, 0.3))',
        boxShadow: 'inset 0 0 30px rgba(147, 51, 234, 0.1), 0 0 40px rgba(147, 51, 234, 0.2)'
      } : {}}
    >
      {/* Pulsing border ring effect when thinking */}
      {isTyping && (
        <div className="absolute inset-0 rounded-l-2xl ring-2 ring-purple-400/30 animate-pulse pointer-events-none" 
             style={{ animationDuration: '2s' }} />
      )}
      {/* Header */}
      <ChatSidebarHeader 
        onToggleCollapse={onToggleCollapse} 
        onReopenTutorial={reopenLastTutorial}
        onStartNewConversation={onStartNewConversation}
        isThinking={isTyping} 
      />

      <ChatContextIndicator 
        currentContext={currentContext}
        darkMode={darkMode}
      />

      {/* Tab Navigation - hidden in portal mode */}
      {showTabs && (
        <ChatTabNavigation 
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          darkMode={darkMode}
        />
      )}

      {/* Tab Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeTab === 'chat' && (
          <ChatMessagesView
            messages={messages}
            isTyping={isTyping}
            autoExpandingMessage={autoExpandingMessage}
            tourInProgress={tourInProgress}
            setTourInProgress={setTourInProgress}
            message={message}
            setMessage={setMessage}
            messagesEndRef={messagesEndRef}
            darkMode={darkMode}
            onSuggestionClick={handleSuggestionClick}
            onExpandMessage={handleExpandMessage}
            onInlineButtonClick={handleInlineButtonClick}
            onSendMessage={handleSendMessage}
            onStopThinking={handleStopThinking}
            placeholder={getContextualPlaceholder(currentContext, tourInProgress, isTyping)}
            currentDomain={currentDomain}
            portalMode={portalMode}
            currentContext={currentContext}
            // Progress state for async polling
            isPolling={isPolling}
            currentProgress={currentProgress}
            currentProgressMessage={currentProgressMessage}
          />
        )}

        {activeTab === 'history' && (
          <ChatHistoryView 
            darkMode={darkMode} 
            onConversationReactivated={onConversationReactivated}
          />
        )}

        {activeTab === 'context' && (
          <ChatContextView 
            currentContext={currentContext}
            darkMode={darkMode}
            onSuggestionClick={handleSuggestionClick}
          />
        )}

        {activeTab === 'settings' && (
          <ChatSettingsView
            hasAnalysis={!!analysisSettings.temporaryAnalysis}
            darkMode={darkMode}
            isDemoUser={analysisSettings.isDemoUser}
            onSave={analysisSettings.onSave}
            onReset={analysisSettings.onReset}
            onLoadSavedAnalysis={analysisSettings.onLoadSavedAnalysis}
            isViewingSavedAnalysis={analysisSettings.isViewingSavedAnalysis}
            refreshTrigger={analysisSettings.refreshTrigger}
            temporaryAnalysis={analysisSettings.temporaryAnalysis}
            onRunAgain={analysisSettings.onRunAgain}
            isAnalysisRunning={analysisSettings.isAnalysisRunning}
            progressiveAnalysisState={analysisSettings.progressiveAnalysisState}
            onRunNewAnalysis={analysisSettings.onRunNewAnalysis}
            showSidebarForm={showSidebarForm}
            onShowSidebarForm={onShowSidebarForm}
            onHideSidebarForm={onHideSidebarForm}
          />
        )}
      </div>
      
      {/* Sparkle Trail Magic ✨ */}
      <SparkleTrail
        isActive={isTrailActive}
        sourceElement={sourceElement}
        targetElement={targetElement}
        onComplete={completeTrail}
        actionType={activeTrail?.actionType}
      />
      
      {/* Choice Cards Modal Magic 🎯 - Rendered via Portal at document.body level */}
      <ChoiceCardsModal
        isOpen={isChoiceCardsModalOpen}
        cards={choiceCardsData?.cards || []}
        preferredChoice={choiceCardsData?.preferred_choice}
        preferenceReasoning={choiceCardsData?.preference_reasoning}
        onCardSelect={handleChoiceCardSelect}
        onClose={handleChoiceCardsClose}
        darkMode={darkMode}
      />
      
      {/* Expanded Message Modal - Rendered via Portal at document.body level */}
      <ExpandedMessageModal
        isOpen={isExpandedMessageModalOpen}
        message={expandedMessage}
        onClose={handleCloseExpandedMessage}
        darkMode={darkMode}
      />

      {/* Task Creation Flow - Rendered via Portal at document.body level */}
      {isTaskFlowActive && activeTaskCard && createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)'
          }}
          onClick={(e) => e.target === e.currentTarget && handleTaskClose()}
        >
          <div className="max-w-2xl w-full max-h-[90vh] overflow-auto">
            <TaskCreationFlow
              choiceCard={activeTaskCard}
              onTaskGenerated={handleTaskGenerated}
              onStepComplete={handleStepComplete}
              onTaskComplete={handleTaskFlowComplete}
              onClose={handleTaskClose}
              darkMode={darkMode}
              animationPreferences={{ enabled: true }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* 🎓 Avatar Journey - "Your consultant follows you" animation */}
      <AvatarJourney
        isActive={isAvatarJourneyActive}
        onComplete={handleAvatarJourneyComplete}
        darkMode={darkMode}
      />

      {/* 🎓 Interactive Tutorial System V2 - Rendered via Portal at document.body level */}
      <InteractiveTutorialV2
        isOpen={isTutorialModeActive}
        tutorialData={activeTutorialData}
        onClose={handleTutorialClose}
        darkMode={darkMode}
      />
    </div>
  );
};

export default ChatSidebarLayout;
